import { type FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ConfigProvider } from "antd";
import enUS from "antd/locale/en_US";
import dayjs, { type Dayjs } from "dayjs";
import { DashboardAntDatePicker } from "src/components/reusable/antdDatePicker/DashboardAntDatePicker";
import { arabicCalendarLocale } from "src/components/reusable/antdDatePicker/arabicCalendarLocale";
import { DEMO_MIN_DATE, DEMO_TODAY } from "../data/restaurantDemoData";
import "./RestaurantDateRangeDisplay.scss";

interface RestaurantDateRangeDisplayProps {
  startDate: string;
  endDate: string;
  // eslint-disable-next-line no-unused-vars
  onRangeChange: (range: { startDate: string; endDate: string }) => void;
}

const RestaurantDateRangeDisplay: FC<RestaurantDateRangeDisplayProps> = ({
  startDate,
  endDate,
  onRangeChange,
}) => {
  const { t, i18n } = useTranslation();
  const isAr = (i18n.resolvedLanguage ?? i18n.language ?? "en").toLowerCase().startsWith("ar");
  const antdLocale = isAr ? arabicCalendarLocale : enUS;

  // dayjs' "DD-MM-YYYY" format always renders Latin digits; format Arabic dates through Intl
  // instead so the picker shows Arabic-Indic numerals like the rest of the dashboard.
  const formatDisplayDate = (date: Dayjs) => {
    if (isAr) {
      return new Intl.DateTimeFormat("ar-EG-u-nu-arab", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        timeZone: "UTC",
      }).format(date.toDate());
    }
    return date.format("DD-MM-YYYY");
  };

  const [localStart, setLocalStart] = useState<Dayjs | null>(() => dayjs(startDate, "YYYY-MM-DD"));
  const [localEnd, setLocalEnd] = useState<Dayjs | null>(() => dayjs(endDate, "YYYY-MM-DD"));

  useEffect(() => {
    setLocalStart(dayjs(startDate, "YYYY-MM-DD"));
    setLocalEnd(dayjs(endDate, "YYYY-MM-DD"));
  }, [startDate, endDate]);

  const minDay = useMemo(() => dayjs(DEMO_MIN_DATE), []);
  const maxDay = useMemo(() => dayjs(DEMO_TODAY), []);

  const handleStartChange = (date: Dayjs | null) => {
    setLocalStart(date);
    if (date && localEnd && !localEnd.isBefore(date, "day")) {
      onRangeChange({ startDate: date.format("YYYY-MM-DD"), endDate: localEnd.format("YYYY-MM-DD") });
    }
  };

  const handleEndChange = (date: Dayjs | null) => {
    setLocalEnd(date);
    if (localStart && date && !date.isBefore(localStart, "day")) {
      onRangeChange({ startDate: localStart.format("YYYY-MM-DD"), endDate: date.format("YYYY-MM-DD") });
    }
  };

  return (
    <ConfigProvider locale={antdLocale} direction={isAr ? "rtl" : "ltr"}>
      <div className="restaurant-date-range-display">
        <DashboardAntDatePicker
          value={localStart}
          format={formatDisplayDate}
          className="dashboard-date-picker"
          allowClear={false}
          placeholder={t("restaurantAnalytics.filters.startDate", { defaultValue: "Start date" })}
          onChange={handleStartChange}
          disabledDate={current =>
            current.isBefore(minDay, "day") ||
            current.isAfter(maxDay, "day") ||
            (localEnd ? current.isAfter(localEnd, "day") : false)
          }
        />
        <span className="restaurant-date-range-display__separator">–</span>
        <DashboardAntDatePicker
          value={localEnd}
          format={formatDisplayDate}
          className="dashboard-date-picker"
          allowClear={false}
          placeholder={t("restaurantAnalytics.filters.endDate", { defaultValue: "End date" })}
          onChange={handleEndChange}
          disabledDate={current =>
            current.isBefore(minDay, "day") ||
            current.isAfter(maxDay, "day") ||
            (localStart ? current.isBefore(localStart, "day") : false)
          }
        />
      </div>
    </ConfigProvider>
  );
};

export default RestaurantDateRangeDisplay;
